import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import HomePage from './pages/HomePage';
import ResultsPage from './pages/ResultsPage';
import ApiDocsPage from './pages/ApiDocsPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminLogin from './pages/admin/AdminLogin';
import { ToastProvider } from './components/Toast';
import { getToken, isAdminAuthenticated, adminFetch, getOrCreateSessionId, ensureOAuth2Token } from './utils/auth';
import { getBackendUrl } from './utils/api';

// Ant Design imports for admin pages
import { ConfigProvider } from 'antd';
import antdAdminTheme from './theme/antdAdminTheme';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Create a React Query client for admin pages
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30000, // 30 seconds
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Console blocking utility
const blockConsole = () => {
  // Check if console blocking is enabled via environment variable
  const blockConsoleEnabled = process.env.REACT_APP_BLOCK_CONSOLE === 'true';
  
  if (!blockConsoleEnabled) return;

  // Override console methods
  const noop = () => {};
  const originalConsole = { ...console };
  
  // Block console methods
  console.log = noop;
  console.warn = noop;
  console.error = noop;
  console.info = noop;
  console.debug = noop;
  console.trace = noop;
  console.table = noop;
  console.group = noop;
  console.groupEnd = noop;
  console.time = noop;
  console.timeEnd = noop;
  console.count = noop;
  console.clear = noop;
  console.dir = noop;
  console.dirxml = noop;
  console.assert = noop;
  console.profile = noop;
  console.profileEnd = noop;
  console.timeStamp = noop;

  // Detect if running on mobile device
  const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
    (window.matchMedia && window.matchMedia('(max-width: 768px)').matches) ||
    ('ontouchstart' in window);

  // Block developer tools detection (only on desktop, not mobile)
  // Mobile devices have natural viewport differences due to address bars and keyboards
  if (!isMobileDevice) {
    let devtoolsOpen = false;
    const threshold = 160;

    setInterval(() => {
      if (window.outerHeight - window.innerHeight > threshold || 
          window.outerWidth - window.innerWidth > threshold) {
        if (!devtoolsOpen) {
          devtoolsOpen = true;
          // Redirect or show warning when dev tools detected
          document.body.innerHTML = `
            <div style="
              display: flex;
              justify-content: center;
              align-items: center;
              height: 100vh;
              background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
              color: white;
              font-family: SF Pro Display, SF Pro Icons, Helvetica Neue, Helvetica, Arial, sans-serif;
              text-align: center;
            ">
              <div>
                <h1>Developer Tools Detected</h1>
                <p>Please close developer tools to continue using this application.</p>
              </div>
            </div>
          `;
        }
      } else {
        devtoolsOpen = false;
      }
    }, 500);
  }

  // Block right-click context menu (only on desktop)
  if (!isMobileDevice) {
    document.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      return false;
    });

    // Block F12, Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+U (only on desktop)
    document.addEventListener('keydown', (e) => {
      if (e.key === 'F12' || 
          (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'J')) ||
          (e.ctrlKey && e.key === 'u')) {
        e.preventDefault();
        return false;
      }
    });

    // Block Ctrl+Shift+C (element selector)
    document.addEventListener('keydown', (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'C') {
        e.preventDefault();
        return false;
      }
    });
  }

  // Disable text selection (but allow on input fields)
  document.addEventListener('selectstart', (e) => {
    // Allow text selection in input, textarea, and contenteditable elements
    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT' || 
        target.tagName === 'TEXTAREA' || 
        target.isContentEditable ||
        target.closest('input') ||
        target.closest('textarea')) {
      return true;
    }
    e.preventDefault();
    return false;
  });

  // Disable drag (but allow on input fields)
  document.addEventListener('dragstart', (e) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT' || 
        target.tagName === 'TEXTAREA' || 
        target.closest('input') ||
        target.closest('textarea')) {
      return true;
    }
    e.preventDefault();
    return false;
  });
};

  // Initialize console blocking on component mount
if (typeof window !== 'undefined') {
    blockConsole();
}

// Protected route component for admin
const ProtectedAdminRoute = ({ children }: { children: React.ReactElement }) => {
  const [isAuthenticated, setIsAuthenticated] = React.useState<boolean | null>(null);
  const [isChecking, setIsChecking] = React.useState(true);

  React.useEffect(() => {
    checkAdminAccess();
  }, []);

  const checkAdminAccess = async () => {
    try {
      // SECURITY: Admin routes require admin session - NO fallback to client tokens
      if (!isAdminAuthenticated()) {
        // No admin session - deny access immediately
        setIsAuthenticated(false);
        setIsChecking(false);
        return;
      }

      // Verify admin session by calling admin profile endpoint (cookie-based auth)
      try {
        const backendUrl = getBackendUrl();
        const apiPath = '/api/auth/admin/me';
        const apiUrl = backendUrl ? `${backendUrl.replace(/\/+$/, '')}${apiPath}` : apiPath;

        const response = await adminFetch(apiUrl);

        // Only allow access if API call succeeds (200 OK)
        // Deny access on any error (network, 500, 401, etc.)
        setIsAuthenticated(response.ok);
      } catch (error) {
        // Network error or API error - deny access for security
        setIsAuthenticated(false);
      } finally {
        setIsChecking(false);
      }
    } catch (error) {
      // Any error - deny access
      setIsAuthenticated(false);
      setIsChecking(false);
    }
  };

  if (isChecking) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-600">Verifying admin access...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect to login page instead of showing error
    return <Navigate to="/console/login" replace />;
  }

  return children;
};

// Wrapper component for admin routes with Ant Design theme
const AdminThemeWrapper = ({ children }: { children: React.ReactNode }) => (
  <QueryClientProvider client={queryClient}>
    <ConfigProvider theme={antdAdminTheme}>
      {children}
    </ConfigProvider>
  </QueryClientProvider>
);

function App() {
  // Initialize OAuth2 token on app startup
  React.useEffect(() => {
    ensureOAuth2Token().catch(() => {});
  }, []);

  // Heartbeat: ping backend every 60s to keep session marked as active
  React.useEffect(() => {
    const sendHeartbeat = () => {
      const sessionId = getOrCreateSessionId();
      fetch(`${getBackendUrl()}/api/search/heartbeat`, {
        headers: { 'X-Session-Id': sessionId },
      }).catch(() => {});
    };

    sendHeartbeat(); // Send immediately on mount
    const interval = setInterval(sendHeartbeat, 60000);
    return () => clearInterval(interval);
  }, []);

  return (
    <ToastProvider>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/results" element={<ResultsPage />} />
        <Route path="/results/multipart" element={<ResultsPage />} />
        <Route path="/api-docs" element={<ApiDocsPage />} />
        <Route
          path="/console/login"
          element={
            <AdminThemeWrapper>
              <AdminLogin />
            </AdminThemeWrapper>
          }
        />
        <Route
          path="/console"
          element={
            <AdminThemeWrapper>
              <ProtectedAdminRoute>
                <AdminDashboard />
              </ProtectedAdminRoute>
            </AdminThemeWrapper>
          }
        />
      </Routes>
    </ToastProvider>
  );
}

export default App;
